// src/pages/LeadQuotes.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Spinner,
  VStack,
  HStack,
  Badge,
  Button,
  Link as ChakraLink,
  useToast,
  Flex,
  Collapse,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import useOrgSettings from '../hooks/useOrgSettings';

function toMillis(createdAt) {
  if (!createdAt) return 0;
  if (typeof createdAt === 'number') return createdAt;
  if (typeof createdAt?.toDate === 'function') return createdAt.toDate().getTime();
  const t = new Date(createdAt).getTime();
  return Number.isFinite(t) ? t : 0;
}
const fmtGBP = (n, currency = 'GBP') => {
  const num = Number(n || 0);
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(num);
  } catch {
    return `£${num.toFixed(2)}`;
  }
};
const itemLabel = (cat, name) => {
  if (name) return name;
  if (cat === 'Windows') return 'Window';
  if (cat === 'Doors') return 'Door';
  if (cat === 'Glass Doors') return 'Glass Door';
  if (cat === 'Works & Extras') return 'Extra';
  return cat || 'Item';
};
const stripFinish = (s) => String(s || '').replace(/\s*Finish\s*$/i, '');

export default function LeadQuotes() {
  const { id: leadId } = useParams();
  const { orgId } = useOrgSettings();
  const navigate = useNavigate();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState('');
  const [open, setOpen] = useState({}); // quoteId -> bool

  useEffect(() => {
    if (!orgId || !leadId) return;

    const load = async () => {
      setLoading(true);
      setInfo('');

      try {
        // 1) Load the lead to find the assigned salesperson
        const leadRef = doc(db, `organizations/${orgId}/leads/${leadId}`);
        const leadSnap = await getDoc(leadRef);
        if (!leadSnap.exists()) {
          setRows([]);
          setInfo('Lead not found.');
          setLoading(false);
          return;
        }

        const spId = leadSnap.data()?.salespersonId;
        if (!spId) {
          setRows([]);
          setInfo('No salesperson assigned — cannot look up quotes.');
          setLoading(false);
          return;
        }

        // 2) Query that salesperson’s quotes (new schema first, then legacy)
        const quotesCol = collection(db, `organizations/${orgId}/salespeople/${spId}/quotes`);
        let s = await getDocs(query(quotesCol, where('leadId', '==', leadId)));
        let items = s.docs.map((d) => ({ id: d.id, path: d.ref.path, ...d.data() }));

        if (items.length === 0) {
          s = await getDocs(query(quotesCol, where('meta.leadId', '==', leadId)));
          items = s.docs.map((d) => ({ id: d.id, path: d.ref.path, ...d.data() }));
        }

        // 3) Sort newest first
        items.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

        setRows(items);
        if (items.length === 0) {
          setInfo('No quotes found for this lead under the assigned salesperson.');
        }
      } catch (e) {
        console.error('[LeadQuotes] load error', e);
        toast({ title: 'Error loading quotes', description: e.message, status: 'error' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orgId, leadId, toast]);

  const toggleOpen = (id) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <Box p={6}>
      {/* Header mirroring LeadDocs with Back to Leads */}
      <Flex justify="space-between" align="center" mb={4} wrap="wrap">
        <Heading size="md">Quotes for Lead</Heading>
        <Button variant="outline" size="sm" onClick={() => navigate('/leads')}>
          Back to Leads
        </Button>
      </Flex>

      {loading ? (
        <HStack>
          <Spinner size="sm" />
          <Text>Loading…</Text>
        </HStack>
      ) : rows.length === 0 ? (
        <Text>{info || 'No quotes found for this lead.'}</Text>
      ) : (
        <VStack align="stretch" spacing={3}>
          {rows.map((q) => {
            const currency = q.meta?.currency || q.currency || 'GBP';
            const items = Array.isArray(q.items) ? q.items : [];
            const subtotal = items.reduce(
              (s, l) => s + Number(l?.lineTotal || Number(l?.unitPrice || 0) * Number(l?.qty || 1)),
              0
            );
            const createdText = q.createdAt
              ? typeof q.createdAt === 'number'
                ? new Date(q.createdAt).toLocaleString()
                : q.createdAt?.toDate
                  ? q.createdAt.toDate().toLocaleString()
                  : '—'
              : '—';

            return (
              <Box key={q.id} p={3} borderWidth="1px" rounded="md">
                <HStack justify="space-between" align="start" wrap="wrap">
                  <Box>
                    <Text fontWeight="semibold">Quote #{q.id.slice(-6)}</Text>
                    <Text fontSize="sm" color="gray.600">
                      Seller: {q.salespersonName || q.spName || '—'}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Created: {createdText}
                    </Text>
                    <Badge colorScheme="purple" mt={1}>
                      Total: {fmtGBP(q.total ?? subtotal, currency)}
                    </Badge>
                  </Box>
                  <HStack>
                    {q.pdfUrl ? (
                      <Button
                        as="a"
                        href={q.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        colorScheme="teal"
                        size="sm"
                      >
                        Open PDF
                      </Button>
                    ) : (
                      <Text fontSize="sm" color="gray.600">
                        No PDF URL saved
                      </Text>
                    )}
                    {items.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleOpen(q.id)}
                        rightIcon={<Icon as={open[q.id] ? ChevronUpIcon : ChevronDownIcon} />}
                      >
                        {open[q.id] ? 'Hide items' : 'Show items'}
                      </Button>
                    )}
                  </HStack>
                </HStack>

                {/* Collapsible items table */}
                <Collapse in={!!open[q.id]} animateOpacity style={{ marginTop: 8 }}>
                  {items.length === 0 ? (
                    <Text fontSize="sm" color="gray.600">
                      No line items.
                    </Text>
                  ) : (
                    <Box overflowX="auto">
                      <Table size="sm" variant="striped">
                        <Thead>
                          <Tr>
                            <Th>Location</Th>
                            <Th>Item</Th>
                            <Th isNumeric>Width</Th>
                            <Th isNumeric>Height</Th>
                            <Th>Style</Th>
                            <Th isNumeric>Openers</Th>
                            <Th isNumeric>FixedLights</Th>
                            <Th>Glazing</Th>
                            <Th>Finish</Th>
                            <Th>Colour</Th>
                            <Th>Material</Th>
                            <Th>Shape</Th>
                            <Th isNumeric>Qty</Th>
                            <Th isNumeric>Each</Th>
                            <Th isNumeric>Line</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {items.map((l, idx) => {
                            const spec = l?.spec || {};
                            const qty = Number(l?.qty || 1);
                            const unit = Number(l?.unitPrice || 0);
                            const line = Number(l?.lineTotal || unit * qty);
                            return (
                              <Tr key={idx}>
                                <Td>{spec.location || '-'}</Td>
                                <Td>{itemLabel(l?.category, l?.name)}</Td>
                                <Td isNumeric>{spec.width_mm ?? '-'}</Td>
                                <Td isNumeric>{spec.height_mm ?? '-'}</Td>
                                <Td>{spec.style || '-'}</Td>
                                <Td isNumeric>{spec.openers ?? '-'}</Td>
                                <Td isNumeric>{spec.fixedLights ?? '-'}</Td>
                                <Td>{spec.glazing || '-'}</Td>
                                <Td>{stripFinish(spec.finish) || '-'}</Td>
                                <Td>{spec.colour || '-'}</Td>
                                <Td>{spec.material || '-'}</Td>
                                <Td>{spec.shape || '-'}</Td>
                                <Td isNumeric>{qty}</Td>
                                <Td isNumeric>{fmtGBP(unit, currency)}</Td>
                                <Td isNumeric>{fmtGBP(line, currency)}</Td>
                              </Tr>
                            );
                          })}
                          <Tr>
                            <Td colSpan={14} textAlign="right" fontWeight="bold">
                              Subtotal
                            </Td>
                            <Td isNumeric fontWeight="bold">
                              {fmtGBP(subtotal, currency)}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </Collapse>

                {q.previewUrl && (
                  <Text mt={2} fontSize="sm">
                    Preview:{' '}
                    <ChakraLink href={q.previewUrl} isExternal>
                      {q.previewUrl}
                    </ChakraLink>
                  </Text>
                )}
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
